import { createContext, useContext, useRef, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";

const ConfirmContext = createContext(() => Promise.resolve(false));

// Promise-based in-app confirmation. Near drop-in for window.confirm:
//   const confirm = useConfirm();
//   if (!(await confirm("Delete this?"))) return;
// Or with options: await confirm({ title, description, confirmText, cancelText, destructive })
export const useConfirm = () => useContext(ConfirmContext);

const DEFAULTS = { title: "Are you sure?", description: "", confirmText: "Confirm", cancelText: "Cancel", destructive: false };

export function ConfirmProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState(DEFAULTS);
  const resolver = useRef(null);

  const confirm = useCallback((options = {}) => {
    const o = typeof options === "string" ? { description: options } : (options || {});
    setOpts({ ...DEFAULTS, ...o });
    setOpen(true);
    return new Promise((resolve) => { resolver.current = resolve; });
  }, []);

  const done = (val) => {
    setOpen(false);
    if (resolver.current) { const r = resolver.current; resolver.current = null; r(val); }
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog open={open} onOpenChange={(o) => { if (!o) done(false); }}>
        <DialogContent className="bg-[#11161F] border-white/10 text-white max-w-md" data-testid="app-confirm-dialog">
          <DialogHeader>
            <div className="flex items-center gap-2.5 mb-1">
              <span className={`flex items-center justify-center w-9 h-9 rounded-full flex-shrink-0 ${opts.destructive ? "bg-rose-500/15 border border-rose-500/30" : "bg-[#2A9D9D]/15 border border-[#2A9D9D]/30"}`}>
                <AlertTriangle className={`w-4.5 h-4.5 ${opts.destructive ? "text-rose-400" : "text-[#2A9D9D]"}`} />
              </span>
              <DialogTitle className="text-lg font-heading font-bold tracking-tight">{opts.title}</DialogTitle>
            </div>
            {opts.description && (
              <DialogDescription className="text-slate-300 text-sm leading-relaxed pt-1 whitespace-pre-line">{opts.description}</DialogDescription>
            )}
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => done(false)} data-testid="app-confirm-cancel"
              className="border-white/10 text-slate-300 hover:bg-white/5 text-xs">
              {opts.cancelText}
            </Button>
            <Button onClick={() => done(true)} data-testid="app-confirm-ok"
              className={`text-xs font-heading font-bold uppercase tracking-wide ${opts.destructive ? "bg-rose-600 hover:bg-rose-500 text-white" : "bg-[#2A9D9D] hover:bg-[#35B5B5] text-white"}`}>
              {opts.confirmText}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
}
